import React, { useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";

const RichTextEditor = ({
    value,
    onChange,
    placeholder = "Start typing...",
}) => {
    const editorRef = useRef(null);

    // Convert content to proper format for TinyMCE
    const getContentForEditor = (content) => {
        if (!content) return "";

        // If it's already HTML, return as is
        if (
            typeof content === "string" &&
            (content.includes("<") || content.includes("&"))
        ) {
            return content;
        }

        // If it's JSON (old format), try to extract text content
        if (typeof content === "string") {
            try {
                const parsed = JSON.parse(content);
                if (parsed.blocks && Array.isArray(parsed.blocks)) {
                    // This is Draft.js format, extract text
                    return parsed.blocks.map((block) => block.text).join("\n");
                }
            } catch (e) {
                // If JSON parsing fails, treat as plain text
                return content;
            }
        }

        return content || "";
    };

    const handleEditorChange = (content) => {
        if (onChange) {
            onChange(content);
        }
    };

    return (
        <div className="rich-text-editor">
            <style jsx>{`
                .rich-text-editor {
                    border: 1px solid #374151;
                    border-radius: 8px;
                    background: #2d2d2d;
                }

                :global(.tox-tinymce) {
                    border: none !important;
                    border-radius: 8px !important;
                }

                :global(.tox-editor-header) {
                    background: #374151 !important;
                    border-bottom: 1px solid #4b5563 !important;
                    border-radius: 8px 8px 0 0 !important;
                }

                :global(.tox-edit-area__iframe) {
                    background: #2d2d2d !important;
                }

                :global(.tox-tinymce .tox-edit-area) {
                    background: #2d2d2d !important;
                }

                .tox .tox-statusbar,
                .tox .tox-toolbar,
                .tox .tox-toolbar__overflow,
                .tox .tox-toolbar__primary,
                .tox:not(.tox-tinymce-inline) .tox-editor-header {
                    background-color: #1a1a1a !important;
                }

                .tox-tinymce {
                    border-color: #1a1a1a !important;
                }

                .tox .tox-tbtn--disabled,
                .tox .tox-tbtn--disabled:hover,
                .tox .tox-tbtn:disabled,
                .tox .tox-tbtn:disabled:hover,
                .tox .tox-tbtn--bespoke,
                .tox .tox-tbtn {
                    background: #2d2d2d;
                }

                :global(.tox-tinymce .tox-edit-area iframe) {
                    background: #2d2d2d !important;
                }
            `}</style>
            <Editor
                onInit={(evt, editor) => (editorRef.current = editor)}
                value={getContentForEditor(value)}
                onEditorChange={handleEditorChange}
                apiKey="m1iaxvqsg7s60apdsj7248csyez6k2rayjja259wyj5bivzc"
                init={{
                    height: 300,
                    menubar: false,
                    plugins: [
                        "advlist",
                        "autolink",
                        "lists",
                        "link",
                        "image",
                        "charmap",
                        "preview",
                        "anchor",
                        "searchreplace",
                        "visualblocks",
                        "code",
                        "fullscreen",
                        "insertdatetime",
                        "media",
                        "table",
                        "help",
                        "wordcount",
                    ],
                    toolbar:
                        "undo redo | blocks | " +
                        "bold italic backcolor | alignleft aligncenter " +
                        "alignright alignjustify | bullist numlist outdent indent | " +
                        "removeformat | help",
                    content_style: `
                        body { 
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
                            font-size: 14px; 
                            color: #f9fafb !important; 
                            background: #2d2d2d !important; 
                            margin: 0 !important;
                            padding: 8px !important;
                        }
                        p { 
                            margin: 0 0 8px 0; 
                            color: #f9fafb !important; 
                        }
                        h1, h2, h3, h4, h5, h6 { 
                            color: #f9fafb !important; 
                            margin: 0.5rem 0; 
                        }
                        ul, ol { 
                            color: #f9fafb !important; 
                            margin: 0 0 8px 0; 
                            padding-left: 24px; 
                        }
                        li { 
                            color: #f9fafb !important; 
                            margin: 4px 0; 
                        }
                        a { 
                            color: #3b82f6 !important; 
                        }
                        blockquote { 
                            border-left: 4px solid #3b82f6; 
                            padding-left: 16px; 
                            margin: 16px 0; 
                            color: #f9fafb !important; 
                        }
                        div, span {
                            color: #f9fafb !important;
                        }
                    `,
                    skin: "oxide-dark",
                    content_css: "dark",
                    placeholder: placeholder,
                    setup: (editor) => {
                        editor.on("init", () => {
                            const body = editor.getBody();
                            body.style.backgroundColor = "#2d2d2d";
                            body.style.color = "#f9fafb";
                            body.style.fontFamily =
                                "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
                            body.style.fontSize = "14px";
                            body.style.padding = "8px";
                            body.style.margin = "0";

                            // Force dark theme on all elements
                            const style = document.createElement("style");
                            style.textContent = `
                                .tox-edit-area iframe {
                                    background: #2d2d2d !important;
                                }
                                .tox-edit-area iframe body {
                                    background: #2d2d2d !important;
                                    color: #f9fafb !important;
                                }
                            `;
                            document.head.appendChild(style);
                        });
                    },
                }}
            />
        </div>
    );
};

export default RichTextEditor;
